import { select, takeEvery } from 'redux-saga/effects';
import { call, put } from 'store/utils';
import * as chimeActions from './chimeActions';
import * as videoActions from 'store/video/videoActions';
import * as messageActions from 'store/message/messageActions';
import * as audioVideoActions from 'store/audioVideo/audioVideoActions';
import * as api from 'utils/apiCalls';
import routes from 'constants/routes.json';
import upstreamVideoQualities from 'constants/upstreamVideoQualities';
import VideoStatus from 'enums/VideoStatus';

export function* chooseAudioOutputDeviceFlowSaga(action) {
  const key = 'chooseAudioOutputDeviceFlowSaga';
  yield put(chimeActions.initAction({ key }));
  try {
    const { audioVideo } = yield select(({ chime }) => ({
      audioVideo: chime.audioVideo,
    }));
    const { device } = action.payload;
    yield audioVideo.chooseAudioOutputDevice(device.value);
    yield put(
      chimeActions.setCurrentDeviceAction({
        deviceType: 'currentAudioOutputDevice',
        deviceValue: device.value,
      })
    );
    yield put(chimeActions.successAction({ key }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key, error }));
  }
}

export function* chooseAudioInputDeviceFlowSaga(action) {
  const key = 'chooseAudioInputDeviceFlowSaga';
  yield put(chimeActions.initAction({ key }));
  try {
    const { audioVideo } = yield select(({ chime }) => ({
      audioVideo: chime.audioVideo,
    }));
    const { device } = action.payload;
    yield audioVideo.chooseAudioInputDevice(device.value);
    yield put(
      chimeActions.setCurrentDeviceAction({
        deviceType: 'currentAudioInputDevice',
        deviceValue: device.value,
      })
    );
    yield put(chimeActions.successAction({ key }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key, error }));
  }
}

export function* chooseVideoInputDeviceFlowSaga(action) {
  const key = 'chooseVideoInputDevice';
  yield put(chimeActions.initAction({ key }));
  try {
    const { audioVideo } = yield select(({ chime }) => ({
      audioVideo: chime.audioVideo,
    }));
    const { device } = action.payload;
    yield audioVideo.chooseVideoInputDevice(device.value);
    yield put(
      chimeActions.setCurrentDeviceAction({
        deviceType: 'currentVideoInputDevice',
        deviceValue: device.value,
      })
    );
    yield put(chimeActions.successAction({ key }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key, error }));
  }
}

export function* startVideoPreviewFlowSaga(action) {
  const key = 'startVideoPreviewFlowSaga';
  yield put(chimeActions.initAction({ key }));
  try {
    const { audioVideo } = yield select(({ chime }) => ({
      audioVideo: chime.audioVideo,
    }));
    const { videoElement } = action.payload;
    yield audioVideo.startVideoPreviewForVideoInput(videoElement);
    yield put(chimeActions.successAction({ key }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key, error }));
  }
}

export function* leaveRoomFlowSaga() {
  const key = 'leaveRoomFlowSaga';
  yield put(chimeActions.initAction({ key }));
  try {
    const { audioVideo, socket, meetingId, externalUserId, externalMeetingId, push } = yield select(
      ({ chime, message, utils }) => ({
        audioVideo: chime.audioVideo,
        socket: message.socket,
        externalMeetingId: chime.externalMeetingId,
        meetingId: chime.meetingId,
        push: utils.push,
        externalUserId: chime.externalUserId,
      })
    );
    audioVideo.stop();
    yield socket.close(10000);
    yield call(api.leaveMeeting, externalMeetingId, meetingId, externalUserId);
    push(routes.HOME);
    yield put(videoActions.resetStateAction());
    yield put(chimeActions.resetStateAction());
    yield put(messageActions.resetStateAction());
    yield put(audioVideoActions.resetStateAction());
    yield put(chimeActions.successAction({ key }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key, error }));
  }
}

export function* chooseUpstreamVideoQualityFlowSaga(action) {
  const key = 'chooseUpstreamVideoQualityFlowSaga';

  yield put(chimeActions.initAction({ key }));

  try {
    const { audioVideo, currentVideoInputDevice, videoStatus } = yield select(
      ({ chime, video }) => ({
        audioVideo: chime.audioVideo,
        currentVideoInputDevice: chime.currentVideoInputDevice,
        videoStatus: video.videoStatus,
      })
    );

    const { id } = action.payload;

    yield put(chimeActions.setUpstreamVideoQualityAction({ id }));

    if (id === -1) {
      // TODO: auto. maybe put the quality adjusting in a timer somewhere?
    } else {
      const { width, height, frameRate, maxBandwidthKbps } = upstreamVideoQualities[id];
      const videoIsOn = videoStatus === VideoStatus.Enabled;

      if (videoIsOn) yield audioVideo.stopLocalVideoTile();

      yield audioVideo.chooseVideoInputQuality(width, height, frameRate, maxBandwidthKbps);

      if (videoIsOn) {
        yield audioVideo.chooseVideoInputDevice(currentVideoInputDevice);
        yield audioVideo.startLocalVideoTile();

        /*if (audioVideo.hasStartedLocalVideoTile()) {
          // TODO: This doesn't work and just removes your video
          yield audioVideo.stopLocalVideoTile();
          yield audioVideo.startLocalVideoTile();
        }*/
      }
    }

    yield put(chimeActions.successAction({ key }));
  } catch (error) {
    yield put(chimeActions.errorAction({ key, error }));
  }
}

export default function* chimeWatcher() {
  yield takeEvery(
    chimeActions.chooseUpstreamVideoQualityAction().type,
    chooseUpstreamVideoQualityFlowSaga
  );
  yield takeEvery(chimeActions.startVideoPreviewAction().type, startVideoPreviewFlowSaga);
  yield takeEvery(
    chimeActions.chooseAudioOutputDeviceAction().type,
    chooseAudioOutputDeviceFlowSaga
  );
  yield takeEvery(chimeActions.chooseAudioInputDeviceAction().type, chooseAudioInputDeviceFlowSaga);
  yield takeEvery(chimeActions.chooseVideoInputDeviceAction().type, chooseVideoInputDeviceFlowSaga);
  yield takeEvery(chimeActions.leaveRoomAction().type, leaveRoomFlowSaga);
}
